import type { RegistrationFormConfiguration, RegistrationFormConfigurationInput } from 'src/generated/graphql-manager';

import {
  Modal,
  Paragraph,
  PrimaryButton,
  SecondaryButton,
  SelectionInputLayout,
  Toggle,
} from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoTooltip } from 'src/components/info-tooltip';

import styles from './registration-fields-modal.module.css';

export type RestrictionType = 'emails' | 'employeeIds' | 'groups';

type RegistrationForm = Omit<RegistrationFormConfiguration, '_typename'>;

type RegistrationFieldsModalProps = {
  registrationForm: RegistrationForm;
  onConfirm: (registrationForm: RegistrationFormConfigurationInput) => void;
  onClose: () => void;
};

export function RegistrationFieldsModal({ onClose, onConfirm, registrationForm }: RegistrationFieldsModalProps) {
  const { t } = useTranslation();

  const initialState: RegistrationForm = {
    firstAndLastName: registrationForm.firstAndLastName,
    jobTitle: registrationForm.jobTitle,
    companyName: registrationForm.companyName,
    departmentName: registrationForm.departmentName,
    phoneNumber: registrationForm.phoneNumber,
    country: registrationForm.country,
    bankCode: registrationForm.bankCode,
  };

  const [formState, setFormState] = useState(initialState);

  const handleOnChange = (key: keyof RegistrationForm) => {
    setFormState((prevState) => ({ ...prevState, [key]: !formState[key] }));
  };

  return (
    <>
      <Modal
        className={styles.modal}
        data-testid="registration-fields-modal"
        title={
          <div className={styles.registrationFieldsModalTitle}>
            {t('manager.webcastSetup.viewerAccess.registration.fieldsModal.title')}
          </div>
        }
        isOpen={true}
        onClose={onClose}
        footer={
          <div className={styles.registrationFieldsModalFooter}>
            <SecondaryButton data-testid={'registration-fields-modal-cancel-button'} onClick={onClose}>
              {t('manager.webcastSetup.viewerAccess.registration.fieldsModal.footer.cancel')}
            </SecondaryButton>

            <PrimaryButton data-testid={'registration-fields-modal-submit-button'} onClick={() => onConfirm(formState)}>
              {t('manager.webcastSetup.viewerAccess.registration.fieldsModal.footer.confirm')}
            </PrimaryButton>
          </div>
        }
      >
        <Paragraph>{t('manager.webcastSetup.viewerAccess.registration.fieldsModal.info')}</Paragraph>

        <div className={styles.content}>
          <SelectionInputLayout
            variant="ghost"
            inputElement={
              <Toggle
                data-testid={'registration-fields-modal-email-toggle'}
                checked
                disabled
                onChange={() => handleOnChange('email')}
              >
                <div className={styles.toggleWrapper}>
                  {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.email')}

                  <InfoTooltip label={t('manager.webcastSetup.viewerAccess.registration.fieldsModal.emailHint')} />
                </div>
              </Toggle>
            }
          />

          <SelectionInputLayout
            variant="ghost"
            inputElement={
              <Toggle
                data-testid={'registration-fields-modal-firstAndLastName-toggle'}
                checked={formState.firstAndLastName || false}
                onChange={() => handleOnChange('firstAndLastName')}
              >
                {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.firstAndLastName')}
              </Toggle>
            }
          />

          <SelectionInputLayout
            variant="ghost"
            inputElement={
              <Toggle
                data-testid={'registration-fields-modal-companyName-toggle'}
                checked={formState.companyName || false}
                onChange={() => handleOnChange('companyName')}
              >
                {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.companyName')}
              </Toggle>
            }
          />

          <SelectionInputLayout
            variant="ghost"
            inputElement={
              <Toggle
                data-testid={'registration-fields-modal-departmentName-toggle'}
                checked={formState.departmentName || false}
                onChange={() => handleOnChange('departmentName')}
              >
                {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.departmentName')}
              </Toggle>
            }
          />

          <SelectionInputLayout
            variant="ghost"
            inputElement={
              <Toggle
                data-testid={'registration-fields-modal-jobTitle-toggle'}
                checked={formState.jobTitle || false}
                onChange={() => handleOnChange('jobTitle')}
              >
                {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.jobTitle')}
              </Toggle>
            }
          />

          <SelectionInputLayout
            variant="ghost"
            inputElement={
              <Toggle
                data-testid={'registration-fields-modal-country-toggle'}
                checked={formState.country || false}
                onChange={() => handleOnChange('country')}
              >
                {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.country')}
              </Toggle>
            }
          />

          <SelectionInputLayout
            variant="ghost"
            inputElement={
              <Toggle
                data-testid={'registration-fields-modal-phoneNumber-toggle'}
                checked={formState.phoneNumber || false}
                onChange={() => handleOnChange('phoneNumber')}
              >
                {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.phoneNumber')}
              </Toggle>
            }
          />

          <SelectionInputLayout
            variant="ghost"
            inputElement={
              <Toggle
                data-testid={'registration-fields-modal-bankCode-toggle'}
                checked={formState.bankCode || false}
                onChange={() => handleOnChange('bankCode')}
              >
                {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.bankCode')}
              </Toggle>
            }
          />
        </div>
      </Modal>
    </>
  );
}
